import { AbstractControl, AsyncValidatorFn, FormControl, ValidationErrors, ValidatorFn } from '@angular/forms'
import { PhoneNumberType, PhoneNumberUtil } from 'google-libphonenumber'
import { map, Observable, startWith, take } from 'rxjs'

const validsPhoneTypes = [PhoneNumberType.MOBILE, PhoneNumberType.FIXED_LINE_OR_MOBILE]

export class GbCustomValidators {
  /**
   * this custom validator is used to check the password and confirm password fields
   * it should set the form as valid once those 2 fields values are the same.
   * @param password form field
   * @param confirmPassword form field
   * @returns ValidatorFn
   */
  public static mustMatch(password: string, confirmPassword: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const _password = control.get(password)?.value
      const _confirmPassword = control.get(confirmPassword)?.value

      if (_password !== _confirmPassword) {
        control.get(confirmPassword)?.setErrors({ noMatch: true })
        return { noMatch: true }
      }
      if (_password.length > 0 && _confirmPassword.length > 0 && _password === _confirmPassword) {
        control.get(confirmPassword)?.setErrors(null)
      }

      return null
    }
  }

  public static emailMatchValidator(email: string | null): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value || !email) {
        return null
      }

      const matches = control.value.toLowerCase() === email.toLowerCase()
      return matches ? { emailMatch: true } : null
    }
  }

  /**
   * Checks if max value is greater than min value
   * @param min
   * @param max
   * @returns ValidatorFn
   */
  public static greaterThan(min: string, max: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const _min = control.get(min)?.value
      const _max = control.get(max)?.value

      if (!_min || !_max) {
        return null
      }

      if (Number(_max) < Number(_min)) {
        control.get(max)?.setErrors({ greaterThan: _min })
        return { greaterThan: _min }
      }

      control.get(max)?.setErrors(null)
      return null
    }
  }

  public static lessThanOrEqualTo(max: string, min: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const _max = control.get(max)?.value
      const _min = control.get(min)?.value

      if (!_max || !_min) {
        return null
      }

      if (Number(_min) > Number(_max)) {
        control.get(min)?.setErrors({ lessThanOrEqualTo: _max })
        return { lessThanOrEqualTo: _max }
      }

      control.get(min)?.setErrors(null)
      return null
    }
  }

  /**
   * this custom validator checks if the the phone number form input is valid
   * @returns ValidatorFn
   */
  public static mobileNumberValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const phone = control.value

      if (!phone) {
        return null
      }
      const phoneUtil = PhoneNumberUtil.getInstance()
      let parsed
      try {
        parsed = phoneUtil.parse(phone)
      } catch (e) {
        return { validPhoneNumber: true }
      }

      if (phoneUtil.isValidNumber(parsed) && validsPhoneTypes.includes(phoneUtil.getNumberType(parsed))) {
        control.setErrors(null)
        return null
      } else {
        return { validPhoneNumber: true }
      }
    }
  }

  public static wholeNumber(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return null
      }

      const regex = /^[0-9][0-9]{0,7}$/
      return regex.test(control.value) ? null : { wholeNumber: false }
    }
  }

  public static upperLowerCase(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return null
      }

      const hasUpperCase = /[A-Z]/.test(control.value)
      const hasLowerCase = /[a-z]/.test(control.value)

      return hasUpperCase && hasLowerCase ? null : { upperLowerCase: true }
    }
  }

  public static allowedUrlCharactersValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const regex = /^[A-Za-z0-9-_]+$/
      const valid = regex.test(control.value)

      return valid ? null : { invalidCharacters: true }
    }
  }

  public static noWhitespaceValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      return (control.value || '').trim().length ? null : { whitespace: true }
    }
  }

  public static lessThanFormControlValidator(
    totalUnitsAvailableFormControl: FormControl,
    unitName: string,
  ): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      return totalUnitsAvailableFormControl.valueChanges.pipe(
        startWith(totalUnitsAvailableFormControl.value || 0),
        take(1),
        map((totalAvailableValue: number) => {
          return control.value <= totalAvailableValue
            ? null
            : { totalUnitsExceeded: totalAvailableValue, unitName: unitName || 'FORMS.ERROR.UNITS' }
        }),
      )
    }
  }
}
